import moment from "moment";

import AppContext from "../app/app-context";
import BaseComponent from "../app/base-component";
import ApiClient from "../api-client/api-client";

import GenericPage from "../app/generic-page/generic-page";
import Table from "../app/table/table";

import FilterButton from "../app/generic-page/buttons/filter-button";

import QuotationPopup from "../calculation/quotation-popup/quotation-popup";

import "./quote-management-page.css";
import QuoteStateSelector from "./quote-state-selector/quote-state-selector";
import TableActionDelete from "../app/table/actions/table-action-delete";
import FilterPopup from "../app/table/filter-popup/filter-popup";
import TableActionSave from "../app/table/actions/table-action-save";
import TableActionRecalculate from "../app/table/actions/table-action-recalculate";

class QuoteManagementPage extends BaseComponent {
  static contextType = AppContext;

  constructor(props) {
    super(props);
    this.state = {
      quotePopupShown: false,
      filterPopupShown: false,
      quotes: null,
      selectedQuote: null,
      accounts: [],
    };
  }

  updateFilterQuery(filterQuery) {
    this.setStateAsync({
      filterQuery,
    }).then(this.refreshData.bind(this));
  }

  updateSortQuery(sortQuery) {
    this.setStateAsync({
      sortQuery,
    }).then(this.refreshData.bind(this));
  }

  componentDidMount() {
    this.getQuoteStates()
      .then(() => this.getAccounts())
      .then(() => this.refreshData());
  }

  getQuoteStates() {
    return this.setStateAsync({
      quoteStates: [
        {
          tag: "OPEN",
          label: "Open",
        },
        {
          tag: "OVERDUE",
          label: "Overdue",
          theme: "red",
          disabled: true,
        },
        {
          tag: "ARCHIVED",
          label: "Archived",
        },
      ],
    });
  }

  getAccounts() {
    return new ApiClient(this.context)
      .call("GET", `/v1/Tenant/${this.context.theme.tenantId}/Account`)
      .then((response) => {
        if (response.ok) {
          let accounts = response.json;

          this.setStateAsync((state) => ({
            accounts: accounts,
          }));
        }
      });
  }

  refreshData() {
    var sortQuery = this.state.sortQuery || "";
    var filterQuery = this.state.filterQuery || "";

    return new ApiClient(this.context)
      .call(
        "GET",
        "/v1/Quote",
        null,
        false,
        {},
        {
          Sorts: sortQuery,
          Filters: filterQuery,
        }
      )
      .then((response) => {
        if (response.ok) {
          let quotes = response.json;

          this.setStateAsync((state) => ({
            quotes: quotes,
          }));
        }
      });
  }

  toggleEditQuotePopup(quote, recalculate = false) {
    this.setState((state) => ({
      quotePopupShown: !state.quotePopupShown,
      selectedQuote: quote,
      recalculate: recalculate,
    }));
  }

  toggleFilterPopup() {
    this.setState((state) => ({
      filterPopupShown: !state.filterPopupShown,
    }));
  }

  renderButtons() {
    return (
      <div>
        <FilterButton onClick={this.toggleFilterPopup.bind(this)} />
      </div>
    );
  }

  renderTable() {
    let translations = this.context.translations;

    return (
      <Table
        mapping={{
          number: {
            label: translations.quotes_quotes_number,
            sortKey: "Number",
          },
          account: {
            label: translations.general_client,
            value: (quote) => quote.account.name,
            sortKey: "AccountName",
          },
          contact: {
            label: translations.general_contact,
            value: (quote) => quote.contact.fullName,
            sortKey: "ContactName",
          },
          title: {
            label: translations.quotes_project_reference,
            sortKey: "Title",
          },
          createdAt: {
            label: translations.general_date,
            value: (quote) => moment(quote.createdAt).format("DD-MM-YYYY"),
            sortKey: "CreatedAt",
          },
          price: {
            label: translations.general_price,
            value: (quote) => "€ " + Math.round(quote.price * 100) / 100,
            sortKey: "Price",
          },
          status: {
            label: translations.general_status,
            value: (quote) => this.renderStatus(quote),
            sortKey: "Status",
          },
          actions: {
            label: translations.general_actions,
            value: (quote) => this.renderActions(quote),
          },
        }}
        entries={this.state.quotes}
        onSelect={(quote) => this.toggleEditQuotePopup(quote)}
        sortQuery={this.state.sortQuery}
        updateSortQuery={this.updateSortQuery.bind(this)}
      />
    );
  }

  updateStatus(quote, status) {
    this.updateStatusToAPI(quote, status);
  }

  updateStatusToAPI(quote, status) {
    let apiClient = new ApiClient(this.context);

    apiClient
      .call("PUT", "/v1/Quote/" + quote.id + "/Status", { status: status })
      .then((result) => {
        if (result.ok) {
          if (status === "CANCELLED") {
            this.context.addNotification(
              "success",
              this.context.translations.notification_quote_deleted
            );
          }
          else {
            this.context.addNotification(
              "success",
              this.context.translations.notification_quote_save_success
            );
          }
        } else {
          throw result;
        }
      })
      .then(() => {
        this.refreshData();
      });
  }

  downloadPdf(quote) {
    let translations = this.context.translations;

    if (!quote.pdfFile) {
      this.context.addNotification("error", translations.notifications_download_unavailable);
      return;
    }

    this.context.addNotification("info", translations.notifications_download_started);

    new ApiClient(this.context)
      .call("GET", quote.pdfFile.filePath)
      .then((response) => {
        this.apiResponseAsDownload(response, `${quote.number}.pdf`);
      });
  }

  recalculate(quote) {
    this.toggleEditQuotePopup(quote, true);
  }

  renderStatus(quote) {
    return (
      <QuoteStateSelector
        value={quote.status}
        mapping={this.state.quoteStates}
        onUpdateStatus={this.updateStatus.bind(this, quote)}
      />
    );
  }

  renderActions(quote) {
    return (
      <div>
        {quote.status === 'OVERDUE' &&
          <TableActionRecalculate
            onClick={this.recalculate.bind(this, quote)}
          />
        }
        <TableActionSave
          onClick={this.downloadPdf.bind(this, quote)}
        />
        <TableActionDelete
          onClick={this.updateStatus.bind(this, quote, "CANCELLED")}
        />
      </div>
    );
  }

  render() {
    let translations = this.context.translations;
    return (
      <GenericPage
        className="quoteManagementPage"
        title={translations.quotes_title}
        buttons={this.renderButtons()}
        content={this.renderTable()}
      >
        {this.state.filterPopupShown && (
          <FilterPopup
            onDismiss={this.toggleFilterPopup.bind(this)}
            filterQuery={this.state.filterQuery}
            updateFilterQuery={this.updateFilterQuery.bind(this)}
            mapping={{
              Number: {
                label: translations.quotes_quotes_number,
              },
              AccountId: {
                label: translations.general_client,
                options: this.state.accounts.map((a) => ({
                  label: a.name,
                  value: a.id,
                })),
              },
              Title: {
                label: translations.quotes_project_reference,
              },
              CreatedAt: {
                label: translations.general_date,
              },
              Status: {
                label: translations.general_status,
                options: this.state.quoteStates.map((m) => ({
                  label: m.label,
                  value: m.tag,
                })),
              },
              Active: null,
            }}
          />
        )}
        {this.state.quotePopupShown && (
          <QuotationPopup
            onDismiss={this.toggleEditQuotePopup.bind(this)}
            quote={this.state.selectedQuote}
            recalculate={this.state.recalculate}
            refreshData={this.refreshData.bind(this)}
          />
        )}
      </GenericPage>
    );
  }
}

export default QuoteManagementPage;
